import { useState } from 'react';
import { Quote } from '../../../mocks/quotes';

interface SendQuoteModalProps {
  quote: Quote;
  onClose: () => void;
  onSent: (quoteId: string) => void;
}

export default function SendQuoteModal({ quote, onClose, onSent }: SendQuoteModalProps) {
  const [channel, setChannel] = useState<'email' | 'whatsapp'>('email');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [customMessage, setCustomMessage] = useState(
    `Dear ${quote.leadName.split(' ')[0]},\n\nPlease find attached your personalised travel quote for ${quote.destination}.\n\nQuote Reference: ${quote.id}\nTotal Amount: AED ${quote.totalAmount.toLocaleString()}\nValid Until: ${new Date(quote.validUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}\n\nWe'd love to help you make this trip a reality. Please feel free to reach out with any questions.\n\nWarm regards,\n${quote.agent}\nHorizon Travel & Tourism`
  );
  const [whatsappMsg, setWhatsappMsg] = useState(
    `Hi ${quote.leadName.split(' ')[0]}! 👋\n\nYour travel quote is ready!\n\n📋 *${quote.title}*\n💰 Total: AED ${quote.totalAmount.toLocaleString()}\n📅 Valid until: ${new Date(quote.validUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}\n\nReply YES to confirm or let me know if you'd like any changes! 😊`
  );

  const handleSend = () => {
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSent(true);
      setTimeout(() => {
        onSent(quote.id);
        onClose();
      }, 1800);
    }, 1500);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
        {sent ? (
          <div className="p-12 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <i className="ri-checkbox-circle-fill text-3xl text-emerald-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Quote Sent!</h3>
            <p className="text-sm text-gray-500">
              Quote {quote.id} has been sent to {quote.leadName} via {channel === 'email' ? 'Email' : 'WhatsApp'}.
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                  <i className="ri-send-plane-line text-base" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Send Quote</p>
                  <p className="text-xs text-gray-400">{quote.id} · {quote.leadName}</p>
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Recipient */}
              <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {quote.leadName.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{quote.leadName}</p>
                  <p className="text-xs text-gray-500">{quote.leadEmail} · {quote.leadPhone}</p>
                </div>
              </div>

              {/* Channel Toggle */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2">Send Via</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setChannel('email')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${channel === 'email' ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-100 text-gray-500 hover:border-gray-200'}`}
                  >
                    <i className="ri-mail-send-line" /> Email
                  </button>
                  <button
                    onClick={() => setChannel('whatsapp')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${channel === 'whatsapp' ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-gray-100 text-gray-500 hover:border-gray-200'}`}
                  >
                    <i className="ri-whatsapp-line" /> WhatsApp
                  </button>
                </div>
              </div>

              {/* Message */}
              {channel === 'email' ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5">Subject Line</label>
                    <input
                      defaultValue={`Your Travel Quote — ${quote.id} | ${quote.destination} | AED ${quote.totalAmount.toLocaleString()}`}
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5">Email Message</label>
                    <textarea
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      rows={7}
                      maxLength={500}
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 resize-none"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <i className="ri-attachment-2 text-amber-500" />
                    <span>PDF quote will be attached automatically</span>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">WhatsApp Message</label>
                  <textarea
                    value={whatsappMsg}
                    onChange={(e) => setWhatsappMsg(e.target.value)}
                    rows={6}
                    maxLength={500}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-emerald-400 resize-none font-mono text-xs leading-relaxed"
                  />
                  <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                    <i className="ri-information-line text-amber-500" />
                    PDF link will be included in the message
                  </p>
                </div>
              )}

              {/* Quote Summary */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">Amount</p>
                  <p className="text-sm font-bold text-amber-600">AED {quote.totalAmount.toLocaleString('en-AE', { maximumFractionDigits: 0 })}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">Valid Until</p>
                  <p className="text-sm font-bold text-gray-700">
                    {new Date(quote.validUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">Items</p>
                  <p className="text-sm font-bold text-gray-700">{quote.items.length}</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap ${channel === 'email' ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-emerald-500 hover:bg-emerald-600 text-white'} disabled:opacity-60`}
              >
                {sending ? (
                  <><i className="ri-loader-4-line animate-spin" /> Sending...</>
                ) : (
                  <><i className={channel === 'email' ? 'ri-mail-send-fill' : 'ri-whatsapp-line'} /> Send via {channel === 'email' ? 'Email' : 'WhatsApp'}</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
